import React from "react";
import { Box, Typography, Tooltip, Chip, alpha } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

import { MathJaxContext, MathJax } from "better-react-mathjax";

const QuizResultsTooltip = ({ quiz, theme }) => {
  const formatTime = (seconds) => {
    if (!seconds) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${remainingSeconds} sekunder`;
    return `${minutes} min${minutes > 1 ? "" : ""} ${remainingSeconds} sek`;
  };

  const calculateResults = (results) => {
    if (
      !results ||
      !results.some((result) => result.selectedOption || result.type === "exam")
    )
      return { correct: 0, wrong: 0 };

    const totalQuestions = results.length;
    const correctAnswers = results.filter((result) => {
      if (result.type === "exam") {
        return result.isCorrect;
      }
      return result.selectedOption === result.correct;
    }).length;

    return { correct: correctAnswers, wrong: totalQuestions - correctAnswers };
  };

  return (
    <>
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
              ["(", ")"],
              ["[", "]"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.3,
            fontFamily: "Arial",
            matchFontHeight: false,
            displayAlign: "left",
            displayIndent: "0",
          },
        }}
      >
        <Tooltip
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          componentsProps={{
            tooltip: {
              sx: {
                width: "300px !important",
                maxWidth: "300px !important",
              },
            },
          }}
          slotProps={{
            popper: {
              sx: {
                "& .MuiTooltip-tooltip": {
                  bgcolor: "background.paper",
                  borderRadius: "8px",
                  width: "300px",
                  maxWidth: "300px !important",
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                },
                "& .MuiTooltip-arrow": {
                  color: "background.paper",
                },
              },
            },
          }}
          title={
            <Box
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                p: 2,
                maxWidth: 400,
                maxHeight: 400,
                overflow: "auto",
                bgcolor: "background.paper",
                borderRadius: 1,
              }}
            >
              {quiz.results.map((result, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2.5,
                    pb: 2.5,
                    "&:not(:last-child)": {
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    },
                  }}
                >
                  <Box sx={{ mb: 1 }}>
                    <Chip
                      size="small"
                      icon={
                        result.isCorrect ? (
                          <CheckCircleOutlineIcon />
                        ) : (
                          <CancelOutlinedIcon />
                        )
                      }
                      label={result.isCorrect ? "Rätt svar" : "Fel svar"}
                      sx={{
                        height: "24px",
                        background: alpha(
                          result.isCorrect ? "#27792B" : "#FF4D4D",
                          0.1,
                        ),
                        color: result.isCorrect ? "#27792B" : "#FF4D4D",
                        fontSize: "11px",
                        fontWeight: 500,
                        "& .MuiChip-icon": {
                          fontSize: 14,
                          color: "inherit",
                        },
                      }}
                    />
                  </Box>

                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Fråga:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1.5,
                      color: "text.secondary",
                    }}
                  >
                    <MathJax>{result.question}</MathJax>
                  </Typography>

                  {result.type === "exam" ? (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        Elevens svar:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1.5,
                          color: "text.secondary",
                        }}
                      >
                        <MathJax>{result.userAnswer}</MathJax>
                      </Typography>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        Förklaring:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1.5,
                          color: "text.secondary",
                        }}
                      >
                        <MathJax>{result.explanation}</MathJax>
                      </Typography>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        Korrekt svar:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        <MathJax>{result.answer}</MathJax>
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        Ditt svar:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1.5,
                          color: "text.secondary",
                        }}
                      >
                        <MathJax>{result.selectedOption}</MathJax>
                      </Typography>
                      {!result.isCorrect && (
                        <>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              mb: 0.5,
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            Rätt svar:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                            }}
                          >
                            <MathJax>{result.correct}</MathJax>
                          </Typography>
                        </>
                      )}
                    </>
                  )}

                  {result.timeSpent && (
                    <Box sx={{ mt: 1.5, mb: 1.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          color: "text.secondary",
                          fontSize: "0.8rem",
                        }}
                      >
                        <TimerOutlinedIcon sx={{ fontSize: "1rem" }} />
                        Tid på frågan: {formatTime(result.timeSpent)}
                      </Typography>
                    </Box>
                  )}

                  {result.hints &&
                    result.revealedHints &&
                    result.revealedHints.length > 0 && (
                      <>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            mt: 1.5,
                            mb: 0.5,
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          Använda ledtrådar ({result.revealedHints.length}/
                          {result.hints.length}):
                        </Typography>
                        {result.revealedHints.map((hintIndex) => (
                          <Typography
                            key={hintIndex}
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <CircleIcon sx={{ fontSize: 8 }} />
                            {result.hints[hintIndex]}
                          </Typography>
                        ))}
                      </>
                    )}
                </Box>
              ))}

              {quiz.results.some((r) => r.timeSpent) && (
                <Box sx={{ mt: 2, mb: 1.5 }}>
                  <Typography
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: "text.secondary",
                      fontSize: "0.8rem",
                    }}
                  >
                    <TimerOutlinedIcon sx={{ fontSize: "1rem" }} />
                    Total tid:{" "}
                    {formatTime(
                      quiz.results.reduce(
                        (total, r) => total + (r.timeSpent || 0),
                        0,
                      ),
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          }
          arrow
          placement="top-start"
        >
          <Box sx={{ display: "flex", gap: 1 }}>
            {(() => {
              const { correct, wrong } = calculateResults(quiz.results);
              return (
                <>
                  <Chip
                    icon={<CheckCircleOutlineIcon />}
                    label={`${correct} rätt`}
                    size="small"
                    sx={{
                      height: "24px",
                      background: alpha("#27792B", 0.1),
                      color: "#27792B",
                      textTransform: "uppercase",
                      fontSize: "11px",
                      fontWeight: 500,
                      "& .MuiChip-icon": {
                        fontSize: 14,
                        color: "#27792B",
                      },
                    }}
                  />
                  <Chip
                    icon={<CancelOutlinedIcon />}
                    label={`${wrong} fel`}
                    size="small"
                    sx={{
                      height: "24px",
                      background: alpha("#FF4D4D", 0.1),
                      color: "#FF4D4D",
                      textTransform: "uppercase",
                      fontSize: "11px",
                      fontWeight: 500,
                      "& .MuiChip-icon": {
                        fontSize: 14,
                        color: "#FF4D4D",
                      },
                    }}
                  />
                </>
              );
            })()}
          </Box>
        </Tooltip>
      </MathJaxContext>
    </>
  );
};

export default QuizResultsTooltip;
